import { useMemo } from "react"
import { Row } from "../../types"
import { TagAssignmentMap } from "../../../../../api/tags"
import {
  AllocationBreakdown,
  FactsheetSummary,
  GroupedData,
  GroupingDimension,
  Position,
  Top10Position,
} from "../types"
import useTagAssignments, { getTagValueForInstrument } from "./useTagAssignments"

const parsePositions = (rows: Row[]): Position[] => {
  return rows.map(row => ({
    display_symbol: String(row.display_symbol || ""),
    company_name: row.company_name ? String(row.company_name) : null,
    market_exposure: Number(row.market_exposure_target ?? row.market_exposure) || 0,
    asset_type: row.asset_type ? String(row.asset_type) : null,
    sector_l1: row.sector_l1 ? String(row.sector_l1) : null,
    sector_l2: row.sector_l2 ? String(row.sector_l2) : null,
    sector_l3: row.sector_l3 ? String(row.sector_l3) : null,
    country_code: row.country_code ? String(row.country_code) : null,
    country_name: row.country_name ? String(row.country_name) : null,
    currency: row.currency && row.currency !== "unknown"
      ? String(row.currency)
      : null,
    instrument_type: String(row.instrument_type || ""),
    underlying_id: row.underlying_id ? Number(row.underlying_id) : null,
    id: row.id ? Number(row.id) : null,
    quantity: Number(row.quantity) || 0,
  }))
}

const computeAllocation = (positions: Position[]): AllocationBreakdown => {
  const result: AllocationBreakdown = {
    equity: 0,
    cash: 0,
    monetary: 0,
    bond: 0,
    alternative: 0,
    unknown: 0,
    total: 0,
  }

  positions.forEach(pos => {
    result.total += pos.market_exposure
    const assetType = pos.asset_type || "unknown"
    if (assetType in result) {
      result[assetType as keyof AllocationBreakdown] += pos.market_exposure
    } else {
      result.unknown += pos.market_exposure
    }
  })

  return result
}

const computeTop10Equities = (positions: Position[], totalExposure: number): Top10Position[] => {
  // Filter to equities only
  const equities = positions.filter(pos => pos.asset_type === "equity")

  // Group by underlying_id (for derivatives) or id (for stocks)
  const byCompany = new Map<string, { symbol: string; companyName: string; exposure: number }>()

  equities.forEach(pos => {
    const key = String(pos.underlying_id || pos.id || pos.display_symbol)
    const existing = byCompany.get(key)
    if (existing) {
      existing.exposure += pos.market_exposure
      // Prefer company_name from stock over derivative
      if (pos.company_name && !pos.underlying_id) {
        existing.companyName = pos.company_name
        existing.symbol = pos.display_symbol
      }
    } else {
      byCompany.set(key, {
        symbol: pos.display_symbol,
        companyName: pos.company_name || pos.display_symbol,
        exposure: pos.market_exposure,
      })
    }
  })

  return Array.from(byCompany.values())
    .sort((a, b) => Math.abs(b.exposure) - Math.abs(a.exposure))
    .slice(0, 10)
    .map(item => ({
      ...item,
      percentage: totalExposure > 0 ? (item.exposure / totalExposure) * 100 : 0,
    }))
}

const computeBreakdown = (
  positions: Position[],
  field: keyof Position,
  totalExposure: number
): { name: string; value: number }[] => {
  const byField = new Map<string, number>()

  positions.forEach(pos => {
    const value = pos[field]
    const key = value ? String(value) : "Unknown"
    byField.set(key, (byField.get(key) || 0) + pos.market_exposure)
  })

  return Array.from(byField.entries())
    .map(([name, exposure]) => ({
      name,
      value: totalExposure > 0 ? (exposure / totalExposure) * 100 : 0,
    }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, 10)
}

const getPositionGroupKey = (
  pos: Position,
  dimension: GroupingDimension,
  tagAssignments: TagAssignmentMap | undefined
): string => {
  if (dimension.startsWith("tag_")) {
    const tagGroupId = Number(dimension.slice(4))
    const instrumentId = pos.underlying_id || pos.id
    const tagValue = getTagValueForInstrument(tagAssignments, instrumentId, tagGroupId)
    return tagValue ?? "Untagged"
  }
  const value = pos[dimension as keyof Position]
  return value ? String(value) : "Unknown"
}

const groupPositions = (
  positions: Position[],
  dimension: GroupingDimension,
  totalExposure: number,
  tagAssignments?: TagAssignmentMap
): GroupedData[] => {
  // Filter to equities only (exclude cash, fees, etc.)
  const equities = positions.filter(pos => pos.asset_type === "equity")
  const equityExposure = equities.reduce((sum, p) => sum + p.market_exposure, 0)

  const byDimension = new Map<string, Position[]>()
  equities.forEach(pos => {
    const key = getPositionGroupKey(pos, dimension, tagAssignments)
    if (!byDimension.has(key)) {
      byDimension.set(key, [])
    }
    byDimension.get(key)!.push(pos)
  })

  return Array.from(byDimension.entries())
    .map(([name, groupPositions]) => {
      const groupExposure = groupPositions.reduce((sum, p) => sum + p.market_exposure, 0)

      // Group by company within dimension (using underlying_id for derivatives, id for stocks)
      const byCompany = new Map<string, Position[]>()
      groupPositions.forEach(pos => {
        const companyKey = String(pos.underlying_id || pos.id || pos.display_symbol)
        if (!byCompany.has(companyKey)) {
          byCompany.set(companyKey, [])
        }
        byCompany.get(companyKey)!.push(pos)
      })

      const companies = Array.from(byCompany.entries())
        .map(([companyKey, companyPositions]) => {
          const companyExposure = companyPositions.reduce(
            (sum, p) => sum + p.market_exposure,
            0
          )
          // Get company name - prefer stock's name over derivative's
          const stockPosition = companyPositions.find(p => !p.underlying_id)
          const displayName =
            stockPosition?.company_name ||
            companyPositions[0]?.company_name ||
            companyPositions[0]?.display_symbol
          return {
            companyName: displayName,
            companyId: stockPosition?.id || companyPositions[0]?.underlying_id || null,
            totalExposure: companyExposure,
            percentage: equityExposure > 0 ? (companyExposure / equityExposure) * 100 : 0,
            positions: companyPositions,
            isExpanded: false,
          }
        })
        .sort((a, b) => Math.abs(b.totalExposure) - Math.abs(a.totalExposure))

      return {
        name,
        totalExposure: groupExposure,
        percentage: equityExposure > 0 ? (groupExposure / equityExposure) * 100 : 0,
        companies,
      }
    })
    .sort((a, b) => Math.abs(b.totalExposure) - Math.abs(a.totalExposure))
}

export const useFactsheetData = (rows: Row[] | undefined, dimension: GroupingDimension) => {
  const { data: tagAssignments } = useTagAssignments()
  const positions = useMemo(() => (rows ? parsePositions(rows) : []), [rows])

  const summary = useMemo((): FactsheetSummary => {
    const allocation = computeAllocation(positions)
    const equities = positions.filter(pos => pos.asset_type === "equity")
    const equityExposure = equities.reduce((sum, p) => sum + p.market_exposure, 0)
    const top10 = computeTop10Equities(positions, equityExposure)
    const geoBreakdown = computeBreakdown(equities, "country_name", equityExposure)
    const sectorBreakdown = computeBreakdown(equities, "sector_l1", equityExposure)
    const currencyBreakdown = computeBreakdown(equities, "currency", equityExposure)

    return { allocation, top10, geoBreakdown, sectorBreakdown, currencyBreakdown }
  }, [positions])

  const groupedData = useMemo(
    () => groupPositions(positions, dimension, summary.allocation.total, tagAssignments),
    [positions, dimension, summary.allocation.total, tagAssignments]
  )

  return { positions, summary, groupedData }
}
